import { Link } from 'react-router-dom';
import { Zap, ShieldCheck } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function ServicesHumanTrust() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Services · AI Security · Human Trust</p>
          <h1 className="text-2xl md:text-[2.25rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl leading-tight">
            Human risk management for the AI era
          </h1>
          <p className="mt-6 text-[1.05rem] text-[#5c5c5c] max-w-2xl leading-relaxed">
            Every AI agent, copilot, and automation ultimately depends on human judgment at critical moments — verifying a request, spotting anomalous behaviour, escalating before damage spreads. RakFort makes that human judgment reliable through behaviour change, simulation-driven instincts, and trust architectures that keep humans in control of AI-era decisions.
          </p>
        </RfPageFrame>
      </div>

      {/* Two service lines */}
      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c] mb-2">Two service lines — both human-centred</h2>
          <p className="text-[#5c5c5c] mb-10 max-w-3xl">
            Existing awareness platforms were built for email phishing. None natively train employees on AI-specific threats: prompt injection in agent workflows, deepfake video conference infiltration, AI-generated synthetic identities, or how to recognise when your own AI agent is behaving anomalously.
          </p>
          <div className="grid md:grid-cols-2 gap-8">

            {/* Service 1: SimStrike */}
            <div className="rf-card p-8 space-y-5 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FAECE7] rounded-lg flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5 text-[#993C1D]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">SimStrike — AI Attack Simulations</h3>
              </div>
              <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                Multi-vector simulation platform purpose-built for AI-era threats. Five attack channels — emails with embedded prompt injection payloads targeting agent workflows, AI-cloned voice calls impersonating executives, deepfake video conference infiltration, AI-personalised SMS/WhatsApp lures, and synthetic identity attacks. Per-user risk scoring, department heat maps, behavioural trend analysis, and compliance reporting for NIS2, DORA, EU AI Act, and ISO 42001.
              </p>
              <div className="flex gap-2 flex-wrap">
                <span className="inline-block text-xs px-2.5 py-1 rounded bg-[#f0faf5] text-[#0F6E56] font-medium">Recurring</span>
                <span className="inline-block text-xs px-2.5 py-1 rounded bg-[#fff8ee] text-[#854F0B] font-medium">Fastest to market</span>
              </div>
            </div>

            {/* Service 2: AI Trust & Readiness Programme */}
            <div className="rf-card p-8 space-y-5 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#E1F5EE] rounded-lg flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#0F6E56]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">AI Trust & Readiness Programme</h3>
              </div>
              <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                A comprehensive programme that builds lasting human readiness across four interconnected pillars:
              </p>
              <div className="space-y-4">
                <div className="border-l-2 border-[#005a9c]/20 pl-4">
                  <h4 className="text-sm font-semibold text-[#2c2c2c]">AI Awareness Academy</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1 leading-relaxed">
                    Role-based training for developers, finance, HR, executives, and OT operators — covering AI-native threats, agent boundary awareness, supply chain poisoning, deepfake CFO fraud, and synthetic identity detection. Delivered as live workshops, async micro-learning in Teams/Slack, and hands-on labs.
                  </p>
                </div>
                <div className="border-l-2 border-[#005a9c]/20 pl-4">
                  <h4 className="text-sm font-semibold text-[#2c2c2c]">Trust Architecture Consulting</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1 leading-relaxed">
                    Map every point where a human must verify, approve, or override an AI action. Build verification protocols, dual-approval flows, and escalation paths. Deliver the "trust playbook" — a living document for AI-era decision-making. 4–8 week engagement.
                  </p>
                </div>
                <div className="border-l-2 border-[#005a9c]/20 pl-4">
                  <h4 className="text-sm font-semibold text-[#2c2c2c]">AI Policy & Compliance</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1 leading-relaxed">
                    Acceptable use policies for AI agents and generative tools. Shadow AI discovery and governance. EU AI Act risk classification (August 2026 deadline), NIS2 training evidence, and DORA documentation — delivered with employee communication playbooks.
                  </p>
                </div>
                <div className="border-l-2 border-[#005a9c]/20 pl-4">
                  <h4 className="text-sm font-semibold text-[#2c2c2c]">AI Security Culture Programme</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1 leading-relaxed">
                    Long-term behaviour transformation. Champion networks, gamified recognition, executive sponsorship structures, and quarterly AI trust exercises. Measured with behavioural KPIs: report rate, verification compliance, escalation speed, shadow AI reduction.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className="inline-block text-xs px-2.5 py-1 rounded bg-[#f0faf5] text-[#0F6E56] font-medium">Recurring</span>
                <span className="inline-block text-xs px-2.5 py-1 rounded bg-[#EEEDFE] text-[#534AB7] font-medium">Flagship</span>
              </div>
            </div>

          </div>
        </RfPageFrame>
      </section>

      {/* Engagement model */}
      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c] mb-8">Engagement model — Land · Expand · Embed</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rf-card p-6 bg-white border-t-4 border-[#5c6d7e]">
              <h3 className="font-semibold text-[#2c2c2c] mb-3">Land</h3>
              <p className="text-sm text-[#5c5c5c] leading-relaxed mb-4">
                TrustScore baseline assessment. 2-week engagement delivering a quantified human risk score, gap analysis, and prioritised roadmap. Fixed-fee project designed to reveal the gaps that sell the next tier.
              </p>
              <span className="inline-block text-xs px-2.5 py-1 rounded bg-[#eef4fb] text-[#185FA5] font-medium">Entry · $15–30K</span>
            </div>
            <div className="rf-card p-6 bg-white border-t-4 border-[#005a9c]">
              <h3 className="font-semibold text-[#2c2c2c] mb-3">Expand</h3>
              <p className="text-sm text-[#5c5c5c] leading-relaxed mb-4">
                AI Trust & Readiness Programme + SimStrike simulation campaigns (quarterly). Annual subscription with monthly micro-training and continuous measurement. Per-employee pricing.
              </p>
              <span className="inline-block text-xs px-2.5 py-1 rounded bg-[#f0faf5] text-[#0F6E56] font-medium">Annual · $8–15/user/mo</span>
            </div>
            <div className="rf-card p-6 bg-white border-t-4 border-[#0F6E56]">
              <h3 className="font-semibold text-[#2c2c2c] mb-3">Embed</h3>
              <p className="text-sm text-[#5c5c5c] leading-relaxed mb-4">
                Full AI Trust & Readiness Programme + AgentReady + Policy & Compliance + AI Incident Response retainer. Dedicated RakFort partner embedded in the organisation. Multi-year contract with maturity milestones.
              </p>
              <span className="inline-block text-xs px-2.5 py-1 rounded bg-[#f0faf5] text-[#0F6E56] font-medium">Multi-year · Custom</span>
            </div>
          </div>
        </RfPageFrame>
      </section>

      {/* Target verticals */}
      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c] mb-8">Four sectors with acute human-trust gaps</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rf-card p-6 bg-white">
              <h3 className="font-semibold text-[#2c2c2c] mb-2">Financial Services</h3>
              <p className="text-sm text-[#5c5c5c] leading-relaxed">
                Deepfake CFO fraud training, DORA Article 13 staff awareness, wire transfer verification protocols, AI agent governance for trading and compliance workflows, cyber insurance evidence. Buyers: CISO, COO, Head of Compliance.
              </p>
            </div>
            <div className="rf-card p-6 bg-white">
              <h3 className="font-semibold text-[#2c2c2c] mb-2">Healthcare</h3>
              <p className="text-sm text-[#5c5c5c] leading-relaxed">
                PHI exposure prevention through human verification, HIPAA-compliant AI agent usage training, clinician AI literacy, synthetic patient record detection. Buyers: CISO, Chief Medical Officer, Compliance.
              </p>
            </div>
            <div className="rf-card p-6 bg-white">
              <h3 className="font-semibold text-[#2c2c2c] mb-2">Manufacturing & Energy</h3>
              <p className="text-sm text-[#5c5c5c] leading-relaxed">
                NIS2 Article 20 mandatory awareness training, OT operator AI boundaries, critical infrastructure verification protocols, shadow AI governance in plant environments. Buyers: CISO, Plant Director, OT Security Lead.
              </p>
            </div>
            <div className="rf-card p-6 bg-white">
              <h3 className="font-semibold text-[#2c2c2c] mb-2">Professional Services</h3>
              <p className="text-sm text-[#5c5c5c] leading-relaxed">
                Client confidentiality with AI agent access, partner-level AI governance training, lawyer/accountant verification protocols, deepfake client impersonation awareness. Buyers: Managing Partner, CISO, Risk Committee.
              </p>
            </div>
          </div>

          <p className="mt-12 text-[0.9375rem] text-[#5c5c5c]">
            Ready to assess your organisation's human risk?{' '}
            <Link to="/contactus" className="text-[#005a9c] font-medium hover:text-[#004a82]">
              Contact us for a consultation
            </Link>.
          </p>
        </RfPageFrame>
      </section>
    </>
  );
}
